import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRoute } from 'wouter';
import { api } from '../lib/api';
import { useDecideDraft, useDraft, useMarkPosted } from '../lib/drafts';
import type { Project } from '../lib/projects';
import { useSetupOutputs } from '../lib/setup';
import { stripBoldMarkers } from '../lib/textUtils';
import { SlidePreview } from '../components/SlidePreview';
import { ProjectHeader } from '../components/ProjectHeader';
import { NextStep } from '../components/NextStep';
import { downloadAllSlidesAsPdf, downloadAllSlidesAsZip, downloadSlidePng, type SlideExport } from '../lib/exportSlides';

export function DraftDetailPage() {
  const [, paramsPosts] = useRoute('/projects/:id/posts/:draftId');
  const [, paramsLegacy] = useRoute('/projects/:id/drafts/:draftId');
  const params = paramsPosts ?? paramsLegacy;
  const projectId = params?.id;
  const draftId = params?.draftId;
  const query = useDraft(projectId, draftId);
  const setupOutputs = useSetupOutputs(projectId);
  const projectQuery = useQuery({
    enabled: !!projectId,
    queryKey: ['projects', projectId],
    queryFn: () => api<{ project: Project }>(`/api/projects/${projectId}`).then((r) => r.project),
  });
  const decide = useDecideDraft(projectId, draftId);
  const post = useMarkPosted(projectId, draftId);
  const [postUrl, setPostUrl] = useState('');
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<'png' | 'zip' | 'pdf' | null>(null);

  function collectSlides(): SlideExport[] {
    return Array.from(document.querySelectorAll<HTMLElement>('[data-slide-canvas]'))
      .map((el) => ({ el, index: Number(el.dataset.slideIndex ?? '0') }))
      .sort((a, b) => a.index - b.index);
  }

  function baseFilename(): string {
    if (!query.data) return 'created-post';
    return stripBoldMarkers(query.data.draft.topicTitle).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'created-post';
  }

  async function onDownloadPngs() {
    if (downloading) return;
    setDownloading('zip');
    try {
      const slides = collectSlides();
      if (slides.length === 1) await downloadSlidePng(slides[0]!, baseFilename());
      else await downloadAllSlidesAsZip(slides, baseFilename());
    } catch (err) {
      console.error('download failed', err);
      window.alert('Download failed — check console for details.');
    } finally {
      setDownloading(null);
    }
  }

  async function onDownloadPdf() {
    if (downloading) return;
    setDownloading('pdf');
    try {
      await downloadAllSlidesAsPdf(collectSlides(), baseFilename());
    } catch (err) {
      console.error('PDF export failed', err);
      window.alert('PDF export failed — check console for details.');
    } finally {
      setDownloading(null);
    }
  }

  async function copyText(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopyToast(label);
      setTimeout(() => setCopyToast(null), 1500);
    } catch {}
  }

  if (!projectId || !draftId) return null;

  const data = query.data;
  const isReel = data?.draft.format === 'reel_vertical';
  const isCarousel = data?.draft.format === 'carousel';
  const artifactLabel = isReel ? 'Reel' : isCarousel ? 'Carousel' : 'Single image';
  const artifactHelper = isReel
    ? 'Vertical reel with synthesized voice and kinetic captions. Download the MP4 and post manually.'
    : isCarousel
      ? 'Preview the carousel, then download it as a LinkedIn PDF or Instagram PNG set.'
      : 'Preview the single-image post, then download it as a PNG.';
  const linkedInAsset = isReel ? 'MP4 + LinkedIn copy' : isCarousel ? 'PDF carousel + LinkedIn copy' : 'PNG post + LinkedIn copy';
  const instagramAsset = isReel ? 'MP4 + Instagram caption' : isCarousel ? 'PNG carousel + Instagram caption' : 'PNG post + Instagram caption';

  return (
    <div className="min-h-screen">
      <ProjectHeader projectId={projectId} page="INSPECTOR" rightSlot={<Link href={`/projects/${projectId}/posts`} className="rounded-md border border-border-color px-3 py-1.5 text-xs font-medium hover:bg-surface-2">← All posts</Link>} />
      {copyToast && <div className="max-w-6xl mx-auto px-4 md:px-6 pt-2 text-xs text-accent-emerald">{copyToast} copied</div>}

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-8 space-y-6">
        <NextStep projectId={projectId} />
        {query.isLoading && <p className="text-sm text-muted">Loading…</p>}
        {query.error && <p className="text-sm text-accent-red">Could not load post.</p>}
        {data && (
          <>
            <section className="rounded-[1.75rem] border border-border-color bg-surface p-4 md:p-6 overflow-hidden relative">
              <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-accent-cyan/10 to-transparent pointer-events-none" />
              <div className="relative grid lg:grid-cols-[1fr_auto] gap-5 items-end">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-accent-cyan mb-3">Created post</p>
                  <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight max-w-3xl">{stripBoldMarkers(data.draft.topicTitle)}</h1>
                  <p className="mt-3 text-sm text-muted max-w-3xl leading-relaxed">
                    {data.draft.format.replace('_', ' ')}{data.draft.angle && ` · ${data.draft.angle}`} · {new Date(data.draft.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 justify-start lg:justify-end">
                  <Status label={data.draft.status} tone={data.draft.status === 'posted' ? 'emerald' : data.draft.status === 'rejected' ? 'red' : data.draft.status === 'approved' ? 'cyan' : 'amber'} />
                  {data.draft.empathyVerdict && <Status label={data.draft.empathyVerdict.replace('_', ' ')} tone={data.draft.empathyVerdict === 'helpful' ? 'emerald' : 'amber'} />}
                  {data.draft.safetyVerdict && <Status label={`safety ${data.draft.safetyVerdict}`} tone={data.draft.safetyVerdict === 'pass' ? 'emerald' : 'red'} />}
                </div>
              </div>
              {data.draft.safetyReasons.length > 0 && <ul className="relative mt-4 text-xs text-accent-red list-disc list-inside">{data.draft.safetyReasons.map((r, i) => <li key={i}>{r}</li>)}</ul>}
              {data.draft.safetyVerdict && (
                <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-muted/70">
                  v1 brand safety uses exact-phrase rules · close variants may slip through
                </p>
              )}
            </section>

            <section className="grid lg:grid-cols-[minmax(0,1.25fr)_360px] gap-5 items-start">
              <div className="rounded-[1.5rem] border border-border-color bg-surface p-4">
                <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
                  <div>
                    <h2 className="font-display text-2xl font-bold tracking-tight">{artifactLabel}</h2>
                    <p className="text-xs text-muted mt-1">{artifactHelper}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isReel ? (
                      data.draft.mediaUrl && (
                        <a href={data.draft.mediaUrl} download className="rounded-md bg-primary text-white px-3 py-1.5 text-xs font-medium hover:bg-primary/90">MP4</a>
                      )
                    ) : (
                      <>
                        <button onClick={onDownloadPngs} disabled={!!downloading} className="rounded-md border border-border-color px-3 py-1.5 text-xs font-medium hover:bg-surface-2 disabled:opacity-50">{downloading === 'zip' ? 'Exporting…' : data.slides.length > 1 ? 'PNG ZIP' : 'PNG'}</button>
                        {data.slides.length > 1 && <button onClick={onDownloadPdf} disabled={!!downloading} className="rounded-md bg-primary text-white px-3 py-1.5 text-xs font-medium hover:bg-primary/90 disabled:opacity-50">{downloading === 'pdf' ? 'Exporting…' : 'PDF'}</button>}
                      </>
                    )}
                  </div>
                </div>
                {isReel ? (
                  <ReelPreview mediaUrl={data.draft.mediaUrl} scriptText={data.draft.scriptText} />
                ) : (
                  <div className={`grid gap-4 ${data.draft.format === 'single_image' ? 'max-w-[560px] mx-auto' : 'sm:grid-cols-2'}`}>
                    {data.slides.map((s) => (
                      <div key={s.id}>
                        <SlidePreview
                          slide={s}
                          totalSlides={data.slides.length}
                          kit={setupOutputs.data?.brandKit ?? null}
                          format={data.draft.format === 'reel_vertical' ? 'carousel' : data.draft.format}
                          projectName={projectQuery.data?.name ?? ''}
                          publicUrl={projectQuery.data?.publicUrl ?? null}
                          projectLogoUrl={projectQuery.data?.logoUrl ?? null}
                          logoIncludesWordmark={projectQuery.data?.logoIncludesWordmark ?? true}
                          topicTitle={data.draft.topicTitle}
                        />
                        <p className="mt-1.5 text-[10px] uppercase tracking-wide text-muted">Slide {s.slideIndex + 1} · {s.kind}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <aside className="space-y-4 lg:sticky lg:top-[88px]">
                {data.draft.linkedinCaption && <CreatorCard label="LinkedIn version" icon="business_center" asset={linkedInAsset} text={data.draft.linkedinCaption} onCopy={() => copyText(data.draft.linkedinCaption!, 'LinkedIn')} />}
                {data.draft.instagramCaption && <CreatorCard label="Instagram version" icon="photo_library" asset={instagramAsset} text={data.draft.instagramCaption} onCopy={() => copyText(data.draft.instagramCaption!, 'Instagram')} />}
                <Actions status={data.draft.status} decide={() => decide.mutate('approved')} reject={() => decide.mutate('rejected')} isDeciding={decide.isPending} postUrl={postUrl} setPostUrl={setPostUrl} markPosted={() => post.mutate(postUrl.trim() || null)} isPosting={post.isPending} postedUrl={data.draft.postedUrl} />
              </aside>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function ReelPreview({ mediaUrl, scriptText }: { mediaUrl: string | null; scriptText: string | null }) {
  if (!mediaUrl) {
    return (
      <div className="rounded-xl border border-border-color bg-surface-2 p-4 md:p-6 text-sm text-muted text-center">
        Reel render in progress. The MP4 will appear here once the content team finishes.
      </div>
    );
  }
  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-4 items-start">
      <div className="rounded-xl border border-border-color bg-black overflow-hidden mx-auto" style={{ width: '100%', maxWidth: 420, aspectRatio: '9 / 16' }}>
        <video controls preload="metadata" src={mediaUrl} className="w-full h-full" />
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted mb-2">Voiceover script</p>
        <div className="rounded-xl border border-border-color bg-surface-2 p-4 text-sm leading-relaxed text-text-primary max-h-[560px] overflow-y-auto whitespace-pre-wrap">
          {scriptText ?? '(no script text recorded)'}
        </div>
      </div>
    </div>
  );
}

function CreatorCard({ label, icon, asset, text, onCopy }: { label: string; icon: string; asset: string; text: string; onCopy: () => void }) {
  return (
    <section className="rounded-[1.25rem] border border-border-color bg-surface p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted">{asset}</p>
          <h2 className="font-display text-xl font-bold mt-1">{label}</h2>
        </div>
        <span className="material-symbols-outlined text-accent-cyan">{icon}</span>
      </div>
      <pre className="rounded-xl border border-border-color bg-background-dark p-3 text-xs whitespace-pre-wrap font-sans leading-relaxed max-h-64 overflow-y-auto scroll-thin">{text}</pre>
      <button onClick={onCopy} className="mt-3 btn-bracket border border-border-color px-3 py-1.5 text-muted hover:text-text-primary hover:border-text-primary">Copy text</button>
    </section>
  );
}

function Actions({ status, decide, reject, isDeciding, postUrl, setPostUrl, markPosted, isPosting, postedUrl }: {
  status: 'pending' | 'approved' | 'rejected' | 'posted';
  decide: () => void;
  reject: () => void;
  isDeciding: boolean;
  postUrl: string;
  setPostUrl: (v: string) => void;
  markPosted: () => void;
  isPosting: boolean;
  postedUrl: string | null;
}) {
  return (
    <section className="rounded-[1.25rem] border border-border-color bg-surface p-4">
      <h2 className="font-display text-xl font-bold mb-3">Review decision</h2>
      {status === 'pending' ? (
        <div className="grid grid-cols-2 gap-2">
          <button onClick={decide} disabled={isDeciding} className="rounded-md bg-accent-emerald text-white px-3 py-2 text-sm font-medium hover:bg-accent-emerald/90 disabled:opacity-50">Approve</button>
          <button onClick={reject} disabled={isDeciding} className="rounded-md bg-accent-red text-white px-3 py-2 text-sm font-medium hover:bg-accent-red/90 disabled:opacity-50">Reject</button>
        </div>
      ) : status === 'approved' ? (
        <div className="space-y-2">
          <input type="url" value={postUrl} onChange={(e) => setPostUrl(e.target.value)} placeholder="Posted URL (optional)" className="w-full rounded-md border border-border-color bg-background-dark px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-cyan" />
          <button onClick={markPosted} disabled={isPosting} className="w-full rounded-md bg-primary text-white px-3 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50">Mark posted</button>
        </div>
      ) : status === 'posted' ? (
        <p className="text-sm text-accent-emerald break-all">{postedUrl ? <>Posted: <a href={postedUrl} target="_blank" rel="noreferrer" className="underline">{postedUrl}</a></> : 'Posted. No URL recorded.'}</p>
      ) : (
        <p className="text-sm text-muted">Rejected. Kept so the system can learn from what did not work.</p>
      )}
    </section>
  );
}

function Status({ label, tone }: { label: string; tone: 'cyan' | 'amber' | 'emerald' | 'red' }) {
  return <span className={`status-pill status-pill-${tone}`}>{label}</span>;
}
